import { pick } from 'lodash-es'
import type { TaskSubmitFormValues } from 'src/components/GenerationTask/BaseTaskSubmitForm'
import { hasUsedTagComplete } from 'src/features/Prompt/store/autoComplete'
import type {
  GenerationModelVersion,
  Media,
  Tack,
} from 'src/services/apollo/generated/graphql'
import { type Tag } from 'src/services/apollo/generated/graphql'
import {
  hasModelEnabledRescaleCfg,
  hasModelEnabledShiftValue,
  isChatModel,
  isLoraFeatureEnabled,
  isMMDIT26AModel,
  isModelCompatibleWithVae,
  modelSupportsCfgScale,
  modelSupportsFaceFix,
  modelSupportsNegativePrompt,
  modelSupportsVae,
} from 'src/utils/model'
import type { AddNullToUndefined } from '~/utils/type'
import { taskParameterFieldsCollection } from './taskParams'

export const mapTackToTag = (
  tack?: AddNullToUndefined<
    Pick<Tack, 'id' | 'defaultName' | 'createdAt' | 'updatedAt' | 'mediaId'>
  >,
): Tag | undefined => {
  if (!tack) return undefined

  return {
    id: tack.id,
    name: tack.defaultName,
    displayName: tack.defaultName,
    mediaId: tack.mediaId || undefined,
    createdAt: tack.createdAt,
    updatedAt: tack.updatedAt,
  }
}

const taskFieldsToTrack = [
  'samplingSteps',
  'cfgScale',
  'hidePrompts',
  'visibility',
  'priority',
  'modelId',
  'batchSize',
  'taskId',
  'activityId',
]

const getTrackAttributes = (values: any) => {
  return {
    ...pick(values, taskFieldsToTrack),
    ...values?.extra,
    size: `${values.width ?? 0}x${values.height ?? 0}`,
    chterId: values.characterId,
  }
}

export const getUpsertArtworkTrackAttrs = (values: any) => {
  return {
    ...getTrackAttributes(values),
    isEdit: !!values?.artworkId,
    hasTitle: !!values?.title?.trim(),
  }
}

export const getCreateTaskTrackAttrs = (
  values: TaskSubmitFormValues & {
    taskId?: string
    activityId?: string
    isHomepage?: boolean

    isWorkflowGenerativeFill?: boolean
    hasBrush?: boolean
    hasBox?: boolean
    hasLasso?: boolean
  },
) => {
  const isGenerativeFill =
    isValuesCnetGenerativeFill(values) || values.isWorkflowGenerativeFill
  return {
    ...getTrackAttributes(values),
    hasQualityTag: values?.qualityTag != null,
    hasIPA: values?.ipAdapter?.enabled ?? false,
    hasBaseImage: values?.baseImage != null,
    hasBaseInpaint: values?.inpaintMaskImage != null,
    isHomepage: !!values?.isHomepage,
    hasBaseControl: values.controlNets != null && values.controlNets.length > 0,
    // ensure lora object is valid
    hasBaseLoRA: isLoraFeatureEnabled(values.lora),
    hasBaseCompo:
      values?.latentCouple != null &&
      Object.keys(values.latentCouple).length > 0,
    hasBaseHires: values?.upscaleDenoisingStrength != null,
    hasEnableTile: !!values?.enableTile,
    hasEnableADetailer: !!values?.enableADetailer,
    hasCustomizedClipSkip: values.clipSkip != null && values.clipSkip !== 2,
    vaeModelId: values?.vaeModelId,

    upscaleMethod:
      (values.enlarge ?? 0) >= 1 && values.enlargeModel
        ? 'upscale'
        : (values.upscale ?? 0) >= 1
          ? 'enhance'
          : 'none',

    fillEditorType: isGenerativeFill ? values.fillEditorType : undefined,
    hasBaseFill: isValuesCnetGenerativeFill(values),
    generativeFillType: isValuesCnetGenerativeFill(values)
      ? values.generativeFillType
      : undefined,
    isWorkflowGenerativeFill: values.isWorkflowGenerativeFill,
    hasBrush:
      values.isWorkflowGenerativeFill && values.fillEditorType === 'inpaint'
        ? values.hasBrush
        : undefined,
    hasBox:
      values.isWorkflowGenerativeFill && values.fillEditorType === 'inpaint'
        ? values.hasBox
        : undefined,
    hasLasso:
      values.isWorkflowGenerativeFill && values.fillEditorType === 'inpaint'
        ? values.hasLasso
        : undefined,

    enablePromptsRewrite: values.enablePromptsRewrite,

    hasAutoComplete: hasUsedTagComplete(),

    hasStyleMix: values.styleMix?.enabled ? true : false,
    hasStyleKey: !!values.modelStyle,

    hasT2i2v: values.t2i2v?.enabled ? true : false,
    t2i2vDuration: values.t2i2v?.enabled
      ? values.t2i2v?.i2vPro?.duration
      : undefined,

    freeGen: values.kaisuukenId ? true : false,
  }
}

const taskParameterFields = [
  ...taskParameterFieldsCollection.common,
  ...taskParameterFieldsCollection.generatorAndI2vProBase,
  ...taskParameterFieldsCollection.generatorBase,
]

type ExtraCreateParameters = {
  baseImageMedia?: Media
  maskImageMedia?: Media
  naturalPrompts?: string
  /** Read compatibility info from modelVersion itself */
  modelVersion?: GenerationModelVersion
}
export const getCreateTaskParameters = (
  values: TaskSubmitFormValues,
  {
    baseImageMedia,
    maskImageMedia,
    naturalPrompts,
    modelVersion,
  }: ExtraCreateParameters = {},
) => {
  const {
    width,
    height,
    lora,
    latentCouple,
    batchSize,
    enlarge,
    enlargeModel,
    upscale,
  } = values

  const isI2i = isValuesI2i(values)
  const isControlNet = isValuesControlNet(values)
  const isCnetGenerativeFill = isValuesCnetGenerativeFill(values)
  const hasMaskMedia = maskImageMedia != null
  const isChatEditing = isChatModel(modelVersion?.modelType)

  // ensure lora object is valid
  const isLora = lora != null && Object.keys(lora).length > 0
  const isLatentCouple =
    latentCouple != null && Object.keys(latentCouple).length > 0
  const isBatchGen = (batchSize ?? 1) > 1
  const isEnlarge = (enlarge ?? 0) >= 1 && enlargeModel
  const isUpscale = (upscale ?? 0) > 1

  const controlNets = isControlNet ? values.controlNets : []

  const hasVaeModel =
    !!values.vaeModelId &&
    isModelCompatibleWithVae(modelVersion?.modelType) &&
    modelSupportsVae(modelVersion?.extra)

  // Filter out params that the current model doesn't support.
  // Values are preserved in form state so users don't lose settings
  // when switching through incompatible models, but they must not
  // leak into API requests.
  const filteredTaskFields = taskParameterFields.filter(field => {
    switch (field) {
      case 'negativePrompts':
        // An active inference profile can override model-level negative
        // prompt support (profile.negativePrompt === 'on'). The UI
        // already gates input visibility via the profile, so let the
        // value through when a profile is selected.
        if (values.inferenceProfile) return true
        return modelSupportsNegativePrompt(modelVersion?.extra)
      case 'cfgScale':
        return modelSupportsCfgScale(modelVersion?.extra)
      default:
        return true
    }
  })

  const canFaceFix =
    modelSupportsFaceFix(modelVersion?.extra) &&
    !isMMDIT26AModel(modelVersion?.modelType)

  const params = {
    ...pick(values, [
      ...filteredTaskFields,

      // add params by different task traits
      ...(isI2i ? taskParameterFieldsCollection.i2iPreProcess : []),
      ...(isLora ? taskParameterFieldsCollection.lora : []),
      ...(isLatentCouple ? taskParameterFieldsCollection.latentCouple : []),
      // upscale / enlarge params are mutually exclusive; enlarge takes precedence
      ...(isEnlarge
        ? taskParameterFieldsCollection.enlarge
        : isUpscale
          ? taskParameterFieldsCollection.upscale
          : []),
      ...(isBatchGen ? taskParameterFieldsCollection.batchGen : []),
      // mask media and model incompatibility both exclude enableADetailer
      ...(!hasMaskMedia && canFaceFix
        ? taskParameterFieldsCollection.aDetailer
        : []),
      // remove vaeModelId field if vaeModelId is invalid (prevent `vaeModelId: ""`)
      ...(hasVaeModel ? taskParameterFieldsCollection.vaeModel : []),
    ]),
    // use size from preset to override size if not i2i / generative fill
    ...(isI2i || isCnetGenerativeFill ? {} : { width, height }),
    mediaId: isI2i ? (baseImageMedia?.id ?? 'STUB') : undefined, // STUB is needed for price calc
    maskMediaId: maskImageMedia?.id,
    controlNets,
    ipAdapter: values.ipAdapter?.enabled
      ? {
          enabled: true,
          referenceImages: values.ipAdapter?.referenceImages,
        }
      : undefined,
    qualityTag: values.qualityTag,
    characterId: values.characterId,
    styleMix: values.styleMix?.enabled ? values.styleMix : undefined,
    modelStyle: values.modelStyle || undefined,
    lightning: values.lightning,
    watermark: values.watermark || undefined,
    t2i2v: values.t2i2v?.enabled ? (values.t2i2v.i2vPro ?? {}) : undefined,
    channel: values.t2i2v?.enabled
      ? (values.t2i2v.channel ?? 'normal')
      : undefined,
    ...(values.extra || naturalPrompts
      ? {
          extra: {
            ...values.extra,
            // Move natrualPrompts to extra
            naturalPrompts,
          },
        }
      : {}),
    ...(naturalPrompts
      ? {
          promptHelper: {
            withStage: true as boolean | undefined,
            userWantToEnable: undefined as boolean | undefined,
            enable: undefined as boolean | undefined,
            forcePromptHelperDetectionSide: undefined as
              | 'server'
              | 'client'
              | undefined,
          },
        }
      : {}),
    chat: isChatEditing
      ? {
          prompts: values.prompts,
          mediaId: values.multiImageReference?.referenceImages?.[0],
          mediaIds: values.multiImageReference?.referenceImages,
          modelId: values.modelId,
          modelConfig: getChatEditingModelConfig(values, modelVersion),
        }
      : undefined,
  }

  const returnParams = {
    ...params,
    ...(!params.width || !params.height ? { width: 512, height: 512 } : {}), // ensure width and height are valid
    ...parseInferenceControl(values, modelVersion),
  }

  // Infer type from returnParams to keep the return type of this method
  // Prevent artworkParamsChecker which uses ReturnType<typeof getCreateTaskParameters>
  // complain about the return type of this method
  if (isChatEditing) {
    if (values.t2i2v?.enabled === true) {
      // Only include other fields when t2i2v is enabled for chat editing
      return {
        ...returnParams,
        chat: params.chat,
      } as typeof returnParams
    }

    return {
      chat: params.chat,
    } as typeof returnParams
  }

  return returnParams
}

/**
 * Only append inferenceControl parameters if model supports it
 * Probably should be refactored using schema validator
 * @param values
 * @returns
 */
function parseInferenceControl(
  values: TaskSubmitFormValues,
  modelVersion?: GenerationModelVersion,
) {
  if (!modelVersion?.extra) return {}

  const canShift = hasModelEnabledShiftValue(modelVersion.extra)
  const canRescaleCfg = hasModelEnabledRescaleCfg(modelVersion.extra)

  // Delete invalid fields
  const inferenceConfig = { ...values.inferenceConfig }

  if (!canShift || !values.inferenceConfig?.modelSampling?.inputs?.shift) {
    delete inferenceConfig['modelSampling']
  }
  if (
    !canRescaleCfg ||
    // null is a valid value
    (values.inferenceConfig?.rescaleCFG !== null &&
      !values.inferenceConfig?.rescaleCFG)
  ) {
    delete inferenceConfig['rescaleCFG']
  }

  // If no fields exist, don't return inferenceConfig object
  if (Object.keys(inferenceConfig).length < 1) return undefined
  return { inferenceConfig }
}

export const isValuesI2i = (values: TaskSubmitFormValues) =>
  values.baseImage != null

export const isValuesControlNet = (values: TaskSubmitFormValues) => {
  if (values.controlNets?.some(cn => cn.type === 'inpaint')) return true
  return (
    values.baseImage == null &&
    values.controlNets != null &&
    values.controlNets.length > 0
  )
}

type Dim = {
  width?: number
  height?: number
}
const DEFAULT_DIM = { width: 512, height: 768 } // TODO: don't hardcode these values
const DIMS = [512, 768, 1024, 1280]
/**
 * replace disabled combination: 512x1024 1024x512
 * @param param0
 * @returns
 */
const replaceDisabledSizes = ({ width, height }: Dim) => {
  if (width === 1024 && height === 512) return { width: 768, height: 512 }
  if (width === 512 && height === 1024) return { width: 512, height: 768 }
  return { width, height }
}
export const getNearestPresetSize = ({ width, height }: Dim): Dim => {
  if (!width || !height) return DEFAULT_DIM

  function getDimIdx(dim: number) {
    const deltas = DIMS.map(d => Math.abs(d - dim))
    const minDelta = Math.min(...deltas)
    const idx = deltas.indexOf(minDelta)
    return idx > -1 ? idx : 0
  }

  return replaceDisabledSizes({
    width: DIMS[getDimIdx(width)],
    height: DIMS[getDimIdx(height)],
  })
}

export const isValuesCnetGenerativeFill = (values: TaskSubmitFormValues) => {
  if ((values.controlNets ?? []).some(cn => cn.type === 'inpaint')) return true
  return false
}

export const isValuesLatentCouple = (values: TaskSubmitFormValues) => {
  return (
    Object.entries(values.latentCouple ?? {}).length > 0 ||
    (values.prompts ?? '').includes('AND')
  )
}

const getChatEditingModelConfig = (
  values: TaskSubmitFormValues,
  modelVersion?: GenerationModelVersion,
) => {
  const { chatEditingConfig } = values
  const chatEditing = modelVersion?.extra?.chatEditing

  const config: {
    resolution?: string
    aspectRatio?: string
    quality?: string
  } = {}

  if (chatEditingConfig?.resolution !== undefined) {
    const supportedResolutionOptions =
      chatEditing?.supportedResolutionOptions as string[] | undefined
    if (supportedResolutionOptions?.includes(chatEditingConfig.resolution)) {
      config.resolution = chatEditingConfig.resolution
    }
  }

  if (chatEditingConfig?.aspectRatio !== undefined) {
    const supportedAspectRatioOptions =
      chatEditing?.supportedAspectRatioOptions as string[] | undefined
    if (supportedAspectRatioOptions?.includes(chatEditingConfig.aspectRatio)) {
      config.aspectRatio = chatEditingConfig.aspectRatio
    }
  }

  if (chatEditingConfig?.quality !== undefined) {
    const supportedQualityOptions = chatEditing?.supportedQualityOptions as
      | string[]
      | undefined
    if (supportedQualityOptions?.includes(chatEditingConfig.quality)) {
      config.quality = chatEditingConfig.quality
    }
  }

  return Object.keys(config).length > 0 ? config : undefined
}
